import { useCallback, useEffect, useMemo, useState } from 'react';

import API from 'my-core/api';
// import API, { param } from 'my-core/api';
import { COUNTRIES, DEFAULT_CURRENCY } from 'my-core/constants';
import { isLsatCourse, LSAC_PREP_PLUS_PRICE } from 'my-core/course-utils';
import { calculateDiscount, getDiscountCodeCheckoutLabel } from 'my-core/discount-code-utils';
import { coursePurchaseFailed } from 'my-core/gtm-events';

import CheckoutLineItems from 'my-components/Checkout/CheckoutLineItems';
import ProductPricesSelect from 'my-components/Checkout/ProductPricesSelect';
import DiscountCodeInput from 'my-components/DiscountCodeInput';
import PaymentForm from 'my-components/PaymentForm';

import Button from '@mui/material/Button';

function CoursePurchaseCheckoutPayment(props) {
  const {
    course,
    currentUser,
    defaultDiscountCode,
    defaultPlan,
    discountCode: discountCodeProp,
    loading,
    noDialog,
    onClose,
    onPaymentSuccess,
    paymentIntent,
    paymentMethod,
    product,
    purchaseError,
    subscription,
    userCourse,
  } = props;

  const isLsat = isLsatCourse(course);
  const [isLsacPrepPlusIncluded, setIsLsacPrepPlusIncluded] = useState(isLsat);

  const [discountCodeState, setDiscountCode] = useState(discountCodeProp);
  const discountCode = discountCodeState || discountCodeProp;

  const currency = course.currency || COUNTRIES[currentUser.country_code]?.currency || DEFAULT_CURRENCY;

  const [activePlan, setActivePlan] = useState(
    () =>
      product?.prices?.find(p => p.id === parseInt(defaultPlan))?.id ||
      product?.prices?.find(p => p.id === subscription?.stripe_price_id)?.id ||
      product?.prices?.[0]?.id,
  );
  const activePriceObj = product?.prices.find(p => p.id === activePlan);

  const origPrice = Number(product ? activePriceObj.amount / 100 : course.price);
  let price = origPrice;

  if (isLsacPrepPlusIncluded) price += LSAC_PREP_PLUS_PRICE;

  const discountCodeDiscount = calculateDiscount(discountCode, price);
  price -= discountCodeDiscount;

  const creditedAmount = Math.min(price, currentUser.course_credits || 0);
  price -= creditedAmount;

  const salesTax = Number(course.currency ? course.sales_tax : COUNTRIES[currentUser.country_code]?.sales_tax) || 0;
  const taxAmount = Math.round(price * salesTax) / 100;
  const total = Math.round((price + taxAmount) * 100) / 100;
  const paymentDetails = {
    lineItems: [
      { label: course.code, amount: origPrice },
      isLsat && {
        label: 'LSAC Prep Plus',
        caption:
          'An LSAT Prep Plus subscription is a required component of your prep. Already paid for an Official LSAT Prep Plus℠ subscription directly with LSAC? Click the "trash" icon to remove.',
        amount: LSAC_PREP_PLUS_PRICE,
        onToggleEnabled: setIsLsacPrepPlusIncluded,
        enabled: isLsacPrepPlusIncluded,
      },
      discountCodeDiscount && {
        label: getDiscountCodeCheckoutLabel(discountCode),
        amount: -discountCodeDiscount,
      },
      creditedAmount && {
        label: 'Credits',
        amount: -creditedAmount,
      },
      taxAmount && { label: `Sales Tax (${salesTax}%)`, amount: taxAmount },
      { label: 'Total', amount: total },
    ].filter(Boolean),
    total,
    tax: taxAmount,
    currency,
  };

  const purchaseOpts = useMemo(
    () => (isLsat ? { include_lsac_prep_plus: isLsacPrepPlusIncluded } : undefined),
    [isLsat, isLsacPrepPlusIncluded],
  );

  const [purchaseIntent, setPurchaseIntent] = useState();
  useEffect(() => {
    if (product || !course.id || !total || ['active', 'pending_payment'].includes(userCourse?.status)) return;

    setPurchaseIntent();
    API.postJSON(`/api/v1/courses/${course.id}/users/create_purchase_intent`, {
      discount_code_id: discountCode?.id,
      opts: purchaseOpts,
    })
      .then(purchaseIntent => {
        setPurchaseIntent(purchaseIntent);
      })
      .catch(error => {
        setPurchaseIntent({ error });
      });
  }, [course.id, discountCode?.id, product, purchaseOpts, total, userCourse]);

  const amountInCents = Math.round(total * 100);
  const priceMatch = amountInCents === purchaseIntent?.amount;

  const completeExistingPayment =
    paymentIntent?.status === 'requires_action' &&
    subscription &&
    subscription.stripe_price_id === activePlan &&
    discountCodeProp?.id === discountCode?.id &&
    amountInCents === paymentIntent.amount;

  const handlePaymentSuccess = useCallback(
    ({ callback, paymentIntent, paymentMethod }) =>
      onPaymentSuccess({
        paymentIntent,
        purchaseOpts,
        total,
        currency,
        callback,
        discountCode,
        paymentMethod,
        plan: activePlan,
        intent: completeExistingPayment && activePriceObj?.recurring ? 'retry_payment' : undefined,
        subscriptionId: subscription?.id,
      }),
    [
      activePlan,
      activePriceObj?.recurring,
      completeExistingPayment,
      currency,
      discountCode,
      onPaymentSuccess,
      purchaseOpts,
      subscription?.id,
      total,
    ],
  );
  const handlePaymentError = useCallback(error => coursePurchaseFailed(course, error), [course]);

  return (
    <>
      {product && (
        <ProductPricesSelect
          activePrice={activePlan}
          css={t => ({ marginBottom: t.spacing(2) })}
          onChange={setActivePlan}
          product={product}
        />
      )}
      <DiscountCodeInput
        ButtonProps={{ variant: 'outlined' }}
        defaultQuery={discountCode?.code || defaultDiscountCode}
        onVerified={setDiscountCode}
        productId={product?.id || course.id}
        productType={product ? 'MyStripe::Product' : 'Course'}
        TextFieldProps={{ placeholder: 'Discount code' }}
        verifyDefaultQuery={!!defaultDiscountCode}
      />
      <CheckoutLineItems css={({ spacing }) => ({ margin: spacing(2, 0) })} paymentDetails={paymentDetails} />
      {total ?
        <PaymentForm
          amount={amountInCents || false}
          countryCode={course.country_code || currentUser.country_code}
          currency={currency}
          disabled={loading || (!product && !priceMatch)}
          error={
            purchaseError ||
            purchaseIntent?.error ||
            (purchaseIntent && !priceMatch && 'Pricing out of date. Please refresh the page')
          }
          onCancel={noDialog && onClose}
          onError={handlePaymentError}
          onSuccess={handlePaymentSuccess}
          paymentIntent={(completeExistingPayment && paymentIntent) || purchaseIntent}
          paymentMethod={paymentMethod || purchaseIntent?.payment_method}
          submitting={loading}
        />
      : <div css={{ textAlign: 'right' }}>
          <Button
            onClick={() => onPaymentSuccess({ purchaseOpts, total, currency, discountCode, plan: activePlan })}
            variant="contained"
          >
            Register
          </Button>
        </div>
      }
    </>
  );
}

export default CoursePurchaseCheckoutPayment;
